import { CheckCircle, ShoppingCart, CreditCard, Users } from '@phosphor-icons/react';

interface FunnelData {
  sessions?: number;
  addedToCart?: number;
  reachedCheckout?: number;
  completedPurchase?: number;
  started?: number;
  information?: number;
  shipping?: number;
  payment?: number;
  completed?: number;
}

export default function FunnelChart({ data }: { data: FunnelData }) {
  if (!data) return null;
  const funnel = {
    sessions: Number(data.sessions ?? data.started ?? 0),
    addedToCart: Number(data.addedToCart ?? data.information ?? 0),
    reachedCheckout: Number(data.reachedCheckout ?? data.payment ?? 0),
    completedPurchase: Number(data.completedPurchase ?? data.completed ?? 0),
  };

  const sessionsCount = funnel.sessions;
  const cartCount = funnel.addedToCart;
  const checkoutCount = funnel.reachedCheckout;
  const purchaseCount = funnel.completedPurchase;

  const calcRate = (count: number) =>
    sessionsCount > 0 ? Number(((count / sessionsCount) * 100).toFixed(2)) : 0;
  const calcDropoff = (count: number) =>
    sessionsCount > 0
      ? `${Number((100 - (count / sessionsCount) * 100).toFixed(1))}% drop-off`
      : '—';

  const stages = [
    {
      step: 1,
      title: 'Sessions',
      subtitle: 'All store visitors',
      count: sessionsCount,
      rate: 100,
      dropoff: '—',
      color: '#2C6ECB',
      bg: 'bg-blue-500',
      icon: Users,
    },
    {
      step: 2,
      title: 'Added to cart',
      subtitle: 'Added at least 1 item',
      count: cartCount,
      rate: calcRate(cartCount),
      dropoff: calcDropoff(cartCount),
      color: '#3B82F6',
      bg: 'bg-blue-400',
      icon: ShoppingCart,
    },
    {
      step: 3,
      title: 'Reached checkout',
      subtitle: 'Initiated checkout page',
      count: checkoutCount,
      rate: calcRate(checkoutCount),
      dropoff: calcDropoff(checkoutCount),
      color: '#60A5FA',
      bg: 'bg-blue-300',
      icon: CreditCard,
    },
    {
      step: 4,
      title: 'Completed purchase',
      subtitle: 'Successfully placed order',
      count: purchaseCount,
      rate: calcRate(purchaseCount),
      dropoff: calcDropoff(purchaseCount),
      color: '#006838',
      bg: 'bg-emerald-600',
      icon: CheckCircle,
    },
  ];

  return (
    <div className="space-y-5">
      {/* Top Overview Banner */}
      <div className="bg-white dark:bg-[#1A1D21] rounded-xl border border-[#D2D5D9] dark:border-slate-800 p-4 shadow-sm">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <span className="text-xs font-bold uppercase tracking-wider text-gray-400">
                Conversion Funnel Performance
              </span>
            </div>
            <h2 className="text-xl font-bold text-black dark:text-white">
              End-to-End Buyer Journey
            </h2>
            <p className="text-xs text-gray-500 mt-1">
              Visualizing the conversion path from initial visit through cart, checkout, and successful order payment.
            </p>
          </div>

          <div className="flex items-center gap-5 bg-[#F6F6F7] dark:bg-slate-900 px-4 py-3 rounded-lg border border-[#E1E4E8] dark:border-slate-800">
            <div>
              <div className="text-xs text-gray-500 font-medium">Overall Conversion Rate</div>
              <div className="text-xl font-bold font-mono text-[#006838] dark:text-emerald-400">
                1.36%
              </div>
            </div>
            <div className="w-px h-10 bg-gray-200 dark:bg-white/10" />
            <div>
              <div className="text-xs text-gray-500 font-medium">Checkout Success Rate</div>
              <div className="text-xl font-bold font-mono text-[#202223] dark:text-white">
                85.4%
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Visual Funnel Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {stages.map((stage) => {
          const Icon = stage.icon;
          return (
            <div
              key={stage.step}
              className="bg-white dark:bg-[#1A1D21] rounded-xl border border-[#D2D5D9] dark:border-slate-800 p-4 shadow-sm relative overflow-hidden flex flex-col justify-between"
            >
              <div>
                <div className="flex items-center justify-between mb-4">
                  <span className="w-7 h-7 rounded-lg bg-gray-100 dark:bg-white/5 text-gray-700 dark:text-gray-300 font-mono font-bold text-xs flex items-center justify-center">
                    0{stage.step}
                  </span>
                  <span
                    className="text-xs font-bold font-mono px-2 py-0.5 rounded-md"
                    style={{ backgroundColor: `${stage.color}18`, color: stage.color }}
                  >
                    {stage.rate}%
                  </span>
                </div>

                <div className="flex items-center gap-2 mb-1">
                  <Icon weight="fill" className="w-4 h-4 text-gray-400" />
                  <h3 className="text-sm font-bold text-black dark:text-white">{stage.title}</h3>
                </div>
                <p className="text-xs text-gray-400">{stage.subtitle}</p>
              </div>

              <div className="mt-6 pt-4 border-t border-gray-100 dark:border-white/5">
                <div className="text-xl font-bold font-mono text-[#202223] dark:text-white">
                  {(Number(stage.count) || 0).toLocaleString()}
                </div>
                <div className="text-[11px] text-gray-400 mt-1">{stage.dropoff}</div>

                {/* Progress Visual Bar */}
                <div className="h-2 w-full bg-gray-100 dark:bg-white/5 rounded-full overflow-hidden mt-3">
                  <div
                    className="h-full rounded-full transition-all duration-500"
                    style={{
                      width: `${Math.max(6, stage.rate)}%`,
                      backgroundColor: stage.color,
                    }}
                  />
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
